import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Search, Filter, Radar, ChevronLeft, ChevronRight, Calendar } from "lucide-react";
import Header from "@/components/homepage/Header";
import { useMarket } from "@/context/MarketContext";
import { signalsArchive, signalsFilters } from "@/services/signalsService";
import { formatArDate, relativeAr } from "@/lib/formatDate";

const PAGE_SIZE = 20;

// Map ISO2 → source-region label used in feed_sources.
const ISO2_TO_REGION_AR = {
  AE: "الإمارات", SA: "السعودية", QA: "قطر", KW: "الكويت", BH: "البحرين",
  OM: "عُمان", JO: "الأردن", EG: "مصر", SY: "سوريا", IQ: "العراق", MA: "المغرب",
};

const FRESHNESS_STYLE = {
  breaking:   "border-emerald-200 bg-emerald-50 text-emerald-700",
  recent:     "border-fursa-200 bg-fursa-50 text-fursa-700",
  historical: "border-surface-200 bg-surface-50 text-ink-500",
  unknown:    "border-surface-200 bg-surface-50 text-ink-500",
};

const SignalCard = ({ item }) => {
  const added = formatArDate(item.fursalogia_added_at);
  const src   = item.source_published_at ? formatArDate(item.source_published_at) : null;
  return (
    <Link
      to={`/signals/${item.id}`}
      data-testid={`signal-card-${item.id}`}
      className="group block rounded-xl border border-surface-200 bg-white p-5 shadow-card transition-all hover:border-fursa-300 hover:shadow-md"
    >
      <div className="flex items-start justify-between gap-3">
        <h3 className="text-base font-semibold leading-snug text-ink-900 group-hover:text-fursa-700">
          {item.title}
        </h3>
        <div className="flex shrink-0 flex-col items-end gap-1">
          {item.country && (
            <span className="rounded-full border border-fursa-200 bg-fursa-50 px-2 py-0.5 text-[10px] font-semibold text-fursa-700">
              {item.country}
            </span>
          )}
          <span
            data-testid={`signal-freshness-${item.id}`}
            className={`rounded-full border px-2 py-0.5 text-[10px] font-semibold ${FRESHNESS_STYLE[item.freshness] || FRESHNESS_STYLE.unknown}`}
          >
            {item.freshness_label}
          </span>
        </div>
      </div>
      {item.summary && (
        <p className="mt-2 line-clamp-2 text-sm leading-relaxed text-ink-500">{item.summary}</p>
      )}
      <div className="mt-3 flex flex-wrap items-center gap-x-3 gap-y-1 border-t border-surface-100 pt-3 text-[11px] text-ink-500">
        <span className="inline-flex items-center gap-1">
          <Radar className="h-3 w-3" strokeWidth={1.75} />
          {item.source_name}
        </span>
        {item.sector && (
          <span className="rounded border border-fursa-200 bg-fursa-50 px-1.5 py-0.5 text-[10px] font-semibold text-fursa-700">
            {item.sector}
          </span>
        )}
        {added && (
          <span className="inline-flex items-center gap-1" title={`أضيف إلى فرصالوجيا: ${added}`}>
            <Calendar className="h-3 w-3" strokeWidth={1.75} />
            {relativeAr(item.fursalogia_added_at)}
          </span>
        )}
        {src && (
          <span className="text-ink-500/70">تاريخ المصدر: {src}</span>
        )}
      </div>
    </Link>
  );
};

export default function SignalsArchivePage() {
  const { market, setMarket, markets } = useMarket();
  const [items, setItems]       = useState([]);
  const [total, setTotal]       = useState(0);
  const [loading, setLoading]   = useState(true);
  const [error, setError]       = useState(null);

  // Toolbar state
  const [q, setQ]               = useState("");
  const [sort, setSort]         = useState("added_desc");
  const [source, setSource]     = useState("");
  const [sector, setSector]     = useState("");
  const [freshness, setFreshness] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo]     = useState("");
  const [page, setPage]         = useState(0);

  // Country is bound to MarketContext; user can override via a small "all" pill.
  const [countryOverride, setCountryOverride] = useState(null); // null = auto (use market), "" = all, "<name>" = pinned

  const [filters, setFilters] = useState({ countries: [], sources: [] });

  const effectiveCountry = useMemo(() => {
    if (countryOverride === "") return "";        // explicit "كل الدول"
    if (countryOverride) return countryOverride;  // explicit override
    return ISO2_TO_REGION_AR[market.iso2] || "";  // follow market
  }, [countryOverride, market]);

  // Load filters once.
  useEffect(() => {
    signalsFilters().then(setFilters).catch(() => {});
  }, []);

  const load = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const data = await signalsArchive({
        q, country: effectiveCountry || undefined, source: source || undefined,
        sector: sector || undefined, freshness: freshness || undefined,
        dateFrom: dateFrom || undefined, dateTo: dateTo || undefined,
        sort, limit: PAGE_SIZE, offset: page * PAGE_SIZE,
      });
      setItems(data.items || []);
      setTotal(data.total || 0);
    } catch (e) {
      setError(String(e));
      setItems([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, [q, effectiveCountry, source, sector, freshness, dateFrom, dateTo, sort, page]);

  useEffect(() => { load(); }, [load]);
  // Reset to first page when a filter changes (but not when sort within page changes).
  useEffect(() => { setPage(0); }, [q, effectiveCountry, source, sector, freshness, dateFrom, dateTo]);

  const pages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  return (
    <>
      <Header />
      <main className="mx-auto min-h-screen w-full max-w-6xl px-4 py-8 sm:px-6 sm:py-12">
        <header className="space-y-2">
          <p className="text-[10px] font-semibold uppercase tracking-widest text-fursa-700">
            SIGNAL ARCHIVE · أرشيف الإشارات
          </p>
          <h1 className="text-3xl font-semibold tracking-tight text-ink-900 sm:text-4xl">
            إشارات السوق
          </h1>
          <p className="max-w-2xl text-sm leading-relaxed text-ink-500">
            أرشيف اقتصادي وتجاري قابل للبحث يضم الإشارات التي التقطتها فرصالوجيا من المصادر الرسميّة والإعلامية.
            ليست كل إشارة فرصة استثمارية — لكنّها جزء من الذاكرة الاقتصادية الحيّة للمنصّة.
          </p>
        </header>

        {/* Toolbar */}
        <section className="mt-6 rounded-xl border border-surface-200 bg-white p-4 shadow-card">
          <div className="flex flex-wrap items-end gap-3">
            {/* Search */}
            <label className="flex min-w-56 flex-1 flex-col gap-1">
              <span className="text-[10px] font-semibold uppercase tracking-widest text-ink-500">بحث</span>
              <div className="relative">
                <Search className="pointer-events-none absolute end-3 top-1/2 h-4 w-4 -translate-y-1/2 text-ink-500" strokeWidth={1.75} />
                <input
                  type="search"
                  value={q}
                  onChange={(e) => setQ(e.target.value)}
                  placeholder="عنوان، ملخص، قطاع…"
                  data-testid="signals-search"
                  className="h-9 w-full rounded-lg border border-surface-200 bg-white ps-3 pe-9 text-sm outline-none placeholder:text-ink-500/60 hover:border-fursa-300 focus:border-fursa-500 focus:ring-2 focus:ring-fursa-500/15"
                />
              </div>
            </label>

            {/* Country */}
            <label className="flex flex-col gap-1">
              <span className="text-[10px] font-semibold uppercase tracking-widest text-ink-500">الدولة</span>
              <select
                value={countryOverride === null ? "__auto__" : countryOverride}
                onChange={(e) => {
                  const v = e.target.value;
                  if (v === "__auto__") setCountryOverride(null);
                  else if (v === "") setCountryOverride("");
                  else setCountryOverride(v);
                }}
                data-testid="signals-filter-country"
                className="h-9 min-w-36 rounded-lg border border-surface-200 bg-white px-2.5 text-xs font-medium text-ink-700 outline-none hover:border-fursa-300 focus:border-fursa-500 focus:ring-2 focus:ring-fursa-500/15"
              >
                <option value="__auto__">حسب السوق: {market.ar}</option>
                <option value="">كل الدول</option>
                {(filters.countries || []).map((c) => (
                  <option key={c.value} value={c.value}>
                    {c.value} · {c.count}
                  </option>
                ))}
              </select>
            </label>

            {/* Sector */}
            <label className="flex flex-col gap-1">
              <span className="text-[10px] font-semibold uppercase tracking-widest text-ink-500">القطاع</span>
              <select
                value={sector}
                onChange={(e) => setSector(e.target.value)}
                data-testid="signals-filter-sector"
                className="h-9 min-w-32 rounded-lg border border-surface-200 bg-white px-2.5 text-xs font-medium text-ink-700 outline-none hover:border-fursa-300 focus:border-fursa-500 focus:ring-2 focus:ring-fursa-500/15"
              >
                <option value="">كل القطاعات</option>
                {(filters.sectors || []).map((s) => (
                  <option key={s.value} value={s.value}>{s.value}</option>
                ))}
              </select>
            </label>

            {/* Freshness */}
            <label className="flex flex-col gap-1">
              <span className="text-[10px] font-semibold uppercase tracking-widest text-ink-500">الحداثة</span>
              <select
                value={freshness}
                onChange={(e) => setFreshness(e.target.value)}
                data-testid="signals-filter-freshness"
                className="h-9 min-w-32 rounded-lg border border-surface-200 bg-white px-2.5 text-xs font-medium text-ink-700 outline-none hover:border-fursa-300 focus:border-fursa-500 focus:ring-2 focus:ring-fursa-500/15"
              >
                <option value="">الكل</option>
                <option value="breaking">عاجل (≤ ٣ أيام)</option>
                <option value="recent">حديث (≤ ٣٠ يوماً)</option>
                <option value="historical">تاريخي</option>
                <option value="unknown">تاريخ غير معروف</option>
              </select>
            </label>

            {/* Source */}
            <label className="flex flex-col gap-1">
              <span className="text-[10px] font-semibold uppercase tracking-widest text-ink-500">المصدر</span>
              <select
                value={source}
                onChange={(e) => setSource(e.target.value)}
                data-testid="signals-filter-source"
                className="h-9 min-w-44 max-w-64 rounded-lg border border-surface-200 bg-white px-2.5 text-xs font-medium text-ink-700 outline-none hover:border-fursa-300 focus:border-fursa-500 focus:ring-2 focus:ring-fursa-500/15"
              >
                <option value="">كل المصادر</option>
                {(filters.sources || []).map((s) => (
                  <option key={s.value} value={s.value}>{s.value}</option>
                ))}
              </select>
            </label>

            {/* Date range */}
            <label className="flex flex-col gap-1">
              <span className="text-[10px] font-semibold uppercase tracking-widest text-ink-500">من</span>
              <input
                type="date"
                value={dateFrom}
                onChange={(e) => setDateFrom(e.target.value)}
                data-testid="signals-filter-date-from"
                dir="ltr"
                className="h-9 rounded-lg border border-surface-200 bg-white px-2.5 text-xs outline-none hover:border-fursa-300 focus:border-fursa-500 focus:ring-2 focus:ring-fursa-500/15"
              />
            </label>
            <label className="flex flex-col gap-1">
              <span className="text-[10px] font-semibold uppercase tracking-widest text-ink-500">إلى</span>
              <input
                type="date"
                value={dateTo}
                onChange={(e) => setDateTo(e.target.value)}
                data-testid="signals-filter-date-to"
                dir="ltr"
                className="h-9 rounded-lg border border-surface-200 bg-white px-2.5 text-xs outline-none hover:border-fursa-300 focus:border-fursa-500 focus:ring-2 focus:ring-fursa-500/15"
              />
            </label>

            {/* Sort */}
            <label className="flex flex-col gap-1">
              <span className="text-[10px] font-semibold uppercase tracking-widest text-ink-500">الترتيب</span>
              <select
                value={sort}
                onChange={(e) => setSort(e.target.value)}
                data-testid="signals-sort"
                className="h-9 min-w-36 rounded-lg border border-surface-200 bg-white px-2.5 text-xs font-medium text-ink-700 outline-none hover:border-fursa-300 focus:border-fursa-500 focus:ring-2 focus:ring-fursa-500/15"
              >
                <option value="added_desc">الأحدث إضافةً</option>
                <option value="added_asc">الأقدم إضافةً</option>
                <option value="source_desc">تاريخ المصدر · الأحدث</option>
                <option value="source_asc">تاريخ المصدر · الأقدم</option>
              </select>
            </label>
          </div>

          <div className="mt-3 flex items-center gap-2 border-t border-surface-100 pt-3 text-[11px] text-ink-500">
            <Filter className="h-3 w-3" strokeWidth={1.75} />
            <span data-testid="signals-total">{total.toLocaleString("ar-EG")} إشارة</span>
            <span className="mx-2 text-ink-500/40">·</span>
            <span>الصفحة {(page + 1).toLocaleString("ar-EG")} من {pages.toLocaleString("ar-EG")}</span>
          </div>
        </section>

        {/* List */}
        <section className="mt-6">
          {loading && items.length === 0 ? (
            <div className="rounded-xl border border-dashed border-surface-200 bg-white p-10 text-center text-sm text-ink-500">
              جارٍ تحميل الإشارات…
            </div>
          ) : error ? (
            <div className="rounded-xl border border-red-200 bg-red-50 p-6 text-sm text-red-700">
              تعذّر تحميل الإشارات — {error}
            </div>
          ) : items.length === 0 ? (
            <div className="rounded-xl border border-dashed border-surface-200 bg-white p-12 text-center">
              <Radar className="mx-auto h-8 w-8 text-fursa-500" strokeWidth={1.5} />
              <h3 className="mt-4 text-lg font-semibold text-ink-900">لا نتائج مطابقة</h3>
              <p className="mt-2 text-sm text-ink-500">جرّب تعديل الفلاتر أو الترتيب.</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 gap-3">
              {items.map((it) => <SignalCard key={it.id} item={it} />)}
            </div>
          )}
        </section>

        {/* Pagination */}
        {pages > 1 && (
          <nav className="mt-6 flex items-center justify-center gap-2" aria-label="التنقل بين الصفحات">
            <button
              type="button"
              onClick={() => setPage((p) => Math.max(0, p - 1))}
              disabled={page === 0 || loading}
              data-testid="signals-page-prev"
              className="inline-flex h-9 items-center gap-1.5 rounded-lg border border-surface-200 bg-white px-3 text-xs font-semibold text-ink-700 shadow-sm transition-colors hover:border-fursa-300 hover:text-fursa-700 disabled:cursor-not-allowed disabled:opacity-40"
            >
              <ChevronRight className="h-3.5 w-3.5" strokeWidth={1.75} />
              السابق
            </button>
            <span dir="ltr" className="font-mono text-xs tabular-nums text-ink-500">
              {page + 1} / {pages}
            </span>
            <button
              type="button"
              onClick={() => setPage((p) => Math.min(pages - 1, p + 1))}
              disabled={page + 1 >= pages || loading}
              data-testid="signals-page-next"
              className="inline-flex h-9 items-center gap-1.5 rounded-lg border border-surface-200 bg-white px-3 text-xs font-semibold text-ink-700 shadow-sm transition-colors hover:border-fursa-300 hover:text-fursa-700 disabled:cursor-not-allowed disabled:opacity-40"
            >
              التالي
              <ChevronLeft className="h-3.5 w-3.5" strokeWidth={1.75} />
            </button>
          </nav>
        )}
      </main>
    </>
  );
}
